<script>
  import { tick } from "svelte";
  import { Checkbox } from "carbon-components-svelte";
  import { TextInput, Button } from "paxray-components-svelte";
  import PlusIcon from "@components/common/icons/app/PlusIcon.svelte";

  export let teams = [];
  export let disabled = false;
  export let targetIds = [];
  export let assignments = new Map();
  export let inheritedTeamIds = new Set();
  export let label = "Subtask";
  export let group = false;
  export let level = "process";
  export let variant = "select";
  export let onAssign = () => {};

  let trigger;
  let panel;
  let searchInput;
  let search = "";
  let open = false;
  let position = "";
  $: options = teams.map((team) => {
    const assigned = assignments.get(String(team.id)) || new Set();
    const count = targetIds.filter((id) => assigned.has(id)).length;
    return {
      ...team,
      checked: targetIds.length > 0 && count === targetIds.length,
      partial: count > 0 && count < targetIds.length,
      count,
    };
  });
  $: selected = options.filter((team) => team.count > 0);
  $: filteredOptions = options.filter((team) =>
    team.name.toLocaleLowerCase().includes(search.toLocaleLowerCase()),
  );
  $: summary = selected.length
    ? selected.length === 1
      ? selected[0].name
      : `${selected.length} teams`
    : "No team";

  async function toggle() {
    if (open) {
      panel.hidePopover();
      return;
    }
    search = "";
    const rect = trigger.getBoundingClientRect();
    const width = Math.min(320, window.innerWidth - 24);
    const left = Math.max(
      12,
      Math.min(rect.right - width, window.innerWidth - width - 12),
    );
    const top = Math.max(
      12,
      Math.min(rect.bottom + 4, window.innerHeight - 340),
    );
    position = `left:${left}px;top:${top}px;width:${width}px;max-height:calc(100vh - ${top + 12}px)`;
    // Inhalt erst beim Oeffnen rendern, da die Chips viele Instanzen pro Seite erzeugen
    open = true;
    panel.showPopover();
    await tick();
    searchInput?.focus();
  }
</script>

{#if variant === "chip"}
  <button
    type="button"
    disabled={disabled || !targetIds.length}
    class="team-assignment-chip"
    bind:this={trigger}
    on:click={toggle}
    aria-expanded={open}
    aria-haspopup="dialog"
    aria-label={`Assign teams to ${level}: ${label}`}
    title={level === "subtask"
      ? `Assign teams to ${label}`
      : `Assign teams to all ${targetIds.length} subtasks in ${label}`}
  >
    <PlusIcon color="currentColor" size={14} /><span>Assign teams</span>
  </button>
{:else}
  <div class="team-assignment-select">
    <button
      type="button"
      {disabled}
      class="team-select-trigger"
      class:empty={!selected.length}
      bind:this={trigger}
      on:click={toggle}
      aria-expanded={open}
      aria-haspopup="dialog"
      aria-label={`Assign teams to ${label}`}
      title={selected.map((team) => team.name).join("\n") || "No team assigned"}
    >
      <span>{summary}</span><span aria-hidden="true">▾</span>
    </button>
  </div>
{/if}
<div
  class="team-assignment-popover"
  bind:this={panel}
  popover="auto"
  role="dialog"
  aria-label={`Teams for ${label}`}
  style={position}
  on:toggle={(event) => {
    open = event.newState === "open";
  }}
>
  {#if open}
    <strong>{group ? `Assign teams to ${level}` : "Assign teams"}</strong>
    <p class="selection-context">{label}</p>
    {#if teams.length >= 3}<TextInput
        type="search"
        labelText="Search teams"
        placeholder="Search teams…"
        bind:value={search}
        bind:ref={searchInput}
      />{/if}
    <div class="team-options">
      {#each filteredOptions as team (team.id)}
        <div class="team-option">
          <Checkbox
            {disabled}
            checked={team.checked}
            indeterminate={team.partial}
            labelText={team.name}
            on:change={(event) =>
              onAssign(String(team.id), targetIds, event.target.checked)}
          />
          {#if inheritedTeamIds.has(String(team.id))}<span class="inherited"
              >Inherited</span
            >{/if}
        </div>
      {:else}<p>No matching teams.</p>{/each}
    </div>
    <Button
      text="Done"
      kind="secondary"
      size="field"
      on:click={() => {
        panel.hidePopover();
        trigger.focus();
      }}
    />
  {/if}
</div>

<style>
  .team-assignment-select {
    display: flex;
    flex-direction: column;
  }
  .team-select-trigger {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    padding: 0.375rem 0.5rem;
    border: 1px solid var(--border-color);
    border-radius: 0.375rem;
    background: var(--pax-white);
    color: var(--pax-black);
    font: inherit;
    text-align: left;
    cursor: pointer;
  }
  .team-select-trigger > span:first-child {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .team-select-trigger.empty {
    background: transparent;
  }
  .team-select-trigger:focus-visible {
    outline: 2px solid var(--pax-black);
    outline-offset: 0.125rem;
  }
  .team-assignment-chip {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 0.25rem;
    padding: 0.125rem 0.5rem;
    border: 1px solid var(--pax-black);
    border-radius: 1rem;
    background: var(--pax-black);
    color: var(--pax-white);
    font: inherit;
    font-size: 0.75rem;
    white-space: nowrap;
    cursor: pointer;
  }
  .team-assignment-chip:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
  .team-assignment-chip:focus-visible {
    outline: 2px solid var(--pax-black);
    outline-offset: 0.125rem;
  }
  .team-assignment-popover {
    position: fixed;
    margin: 0;
    padding: 0.875rem;
    box-sizing: border-box;
    border: 1px solid var(--border-color);
    border-radius: 0.5rem;
    background: var(--pax-white);
    color: var(--pax-black);
    box-shadow: 0 0.25rem 1rem #0002;
    overflow: auto;
    font-size: 0.8125rem;
  }
  .selection-context {
    margin: 0.375rem 0 0.75rem;
    overflow-wrap: anywhere;
  }
  .team-options {
    max-height: 10rem;
    overflow: auto;
    margin-top: 0.5rem;
  }
  .team-option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    padding: 0.375rem 0;
  }
  .team-option :global(.bx--checkbox-wrapper) {
    margin: 0;
  }
  .team-option :global(.bx--checkbox-label-text) {
    font-size: 0.8125rem;
  }
  .inherited {
    font-size: 0.75rem;
  }
</style>
